import clsx from 'clsx';
import type { ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { WEEKDAY_SHORT, type Weekday } from '@doit/shared';

const controlBase =
  'w-full rounded-lg border bg-surface px-3 py-2.5 text-sm text-ink transition focus:outline-none focus:ring-3';
const controlOk = 'border-line-strong focus:border-brand-600 focus:ring-brand-100';
const controlBad = 'border-danger focus:border-danger focus:ring-danger-soft';

function Wrapper({
  label,
  error,
  hint,
  htmlFor,
  children,
}: {
  label?: string;
  error?: string;
  hint?: ReactNode;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={htmlFor} className="block text-[13px] font-medium text-ink-soft">
          {label}
        </label>
      )}
      {children}
      {error ? (
        <p className="text-[12px] text-danger">{error}</p>
      ) : hint ? (
        <p className="text-[12px] text-ink-mute">{hint}</p>
      ) : null}
    </div>
  );
}

// ============================================================
// Select
// ============================================================

type Option = { value: string; label: string; disabled?: boolean };

type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> & {
  label?: string;
  error?: string;
  hint?: string;
  options: Option[];
  placeholder?: string;
};

export function Select({
  label,
  error,
  hint,
  options,
  placeholder,
  className,
  id,
  ...rest
}: SelectProps) {
  const selectId = id ?? (label ? `s-${label.replace(/\s+/g, '-')}` : undefined);
  return (
    <Wrapper label={label} error={error} hint={hint} htmlFor={selectId}>
      <select
        {...rest}
        id={selectId}
        className={clsx(controlBase, error ? controlBad : controlOk, 'pr-8', className)}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
    </Wrapper>
  );
}

// ============================================================
// Textarea
// ============================================================

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: string;
  error?: string;
  hint?: string;
};

export function Textarea({ label, error, hint, className, id, ...rest }: TextareaProps) {
  const areaId = id ?? (label ? `t-${label.replace(/\s+/g, '-')}` : undefined);
  return (
    <Wrapper label={label} error={error} hint={hint} htmlFor={areaId}>
      <textarea
        {...rest}
        id={areaId}
        className={clsx(controlBase, error ? controlBad : controlOk, 'resize-y', className)}
      />
    </Wrapper>
  );
}

// ============================================================
// Checkbox
// ============================================================

export function Checkbox({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  hint?: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 size-4 shrink-0 accent-brand-600"
      />
      <span>
        <span className="block text-[13px] font-medium text-ink">{label}</span>
        {hint && <span className="block text-[12px] text-ink-mute">{hint}</span>}
      </span>
    </label>
  );
}

// ============================================================
// კვირის დღეების ამრჩევი
// ============================================================

const DAYS: Weekday[] = [1, 2, 3, 4, 5, 6, 7];

export function WeekdayPicker({
  value,
  onChange,
  label = 'გაკვეთილის დღეები',
  error,
  hint,
}: {
  value: number[];
  onChange: (days: number[]) => void;
  label?: string;
  error?: string;
  hint?: string;
}) {
  function toggle(day: number) {
    onChange(
      value.includes(day) ? value.filter((d) => d !== day) : [...value, day].sort((a, b) => a - b),
    );
  }

  return (
    <Wrapper label={label} error={error} hint={hint}>
      <div className="flex flex-wrap gap-1.5">
        {DAYS.map((d) => {
          const active = value.includes(d);
          return (
            <button
              key={d}
              type="button"
              onClick={() => toggle(d)}
              aria-pressed={active}
              className={clsx(
                'min-w-11 rounded-lg border px-2.5 py-1.5 text-[12px] font-medium transition',
                active
                  ? 'border-brand-600 bg-brand-600 text-white'
                  : 'border-line-strong bg-surface text-ink-soft hover:border-brand-300 hover:text-ink',
              )}
            >
              {WEEKDAY_SHORT[d]}
            </button>
          );
        })}
      </div>
    </Wrapper>
  );
}

// ============================================================
// სეგმენტური გადამრთველი (ფილტრებისთვის)
// ============================================================

export function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div className="inline-flex rounded-lg border border-line-strong bg-surface p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={clsx(
            'rounded-md px-3 py-1.5 text-[12px] font-medium transition',
            value === o.value ? 'bg-brand-600 text-white' : 'text-ink-soft hover:text-ink',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
